"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useEffect, useId, useRef, useState } from "react";
import { getComponent } from "../../../../client";
import { DaylightAction } from "../DaylightAction/DaylightAction";
import { DaylightMedia } from "../DaylightMedia/DaylightMedia";
import type { IMegaMenuNavigationProps } from "./MegaMenuNavigation.props";

export const MegaMenuNavigation = ({ actions, items, logo, sticky }: IMegaMenuNavigationProps) => {
  const Link = getComponent("Link");
  const [openDesktop, setOpenDesktop] = useState<number | null>(null);
  const [openMobile, setOpenMobile] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  const headerRef = useRef<HTMLElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const navigationId = useId().replaceAll(":", "");

  useEffect(() => setCurrentPath(window.location.pathname), []);

  const isCurrentLink = (href: string) => {
    if (!currentPath || !href.startsWith("/")) return false;
    const pathname = href.split(/[?#]/, 1)[0]?.replace(/\/$/, "") || "/";
    const current = currentPath.replace(/\/$/, "") || "/";
    return pathname === "/"
      ? current === "/"
      : current === pathname || current.startsWith(`${pathname}/`);
  };
  const containsCurrentLink = (item: NonNullable<typeof items>[number]) => {
    if (item.blockType === "megaMenuDirectLink") return isCurrentLink(item.link.href);
    if (item.blockType === "megaMenuDropdown")
      return item.links.some((link) => isCurrentLink(link.href));
    return item.groupItems.some((group) => group.links.some((link) => isCurrentLink(link.href)));
  };

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setOpenDesktop(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenDesktop(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (drawerOpen && !dialog.open) dialog.showModal();
    if (!drawerOpen && dialog.open) dialog.close();
  }, [drawerOpen]);
  const navigate = () => {
    setDrawerOpen(false);
    setOpenMobile(null);
  };
  const linkClass =
    "relative inline-flex min-h-11 items-center px-3 font-daylight-display text-sm font-bold text-foreground transition-colors after:absolute after:bottom-1 after:left-3 after:right-3 after:h-px after:origin-left after:scale-x-0 after:bg-cobalt after:transition-transform hover:text-primary aria-[current=page]:text-cobalt aria-[current=page]:after:scale-x-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
  const nestedLink = (
    link: {
      accessibilityLabel?: string | null;
      href: string;
      label: string;
      openInNewTab?: boolean | null;
    },
    key: number,
    mobile = false,
  ) => (
    <Link
      aria-current={isCurrentLink(link.href) ? "page" : undefined}
      aria-label={link.accessibilityLabel || undefined}
      className={cn(linkClass, "w-full")}
      href={link.href}
      key={key}
      onClick={mobile ? navigate : undefined}
      rel={link.openInNewTab ? "noopener noreferrer" : undefined}
      target={link.openInNewTab ? "_blank" : undefined}
    >
      {link.label}
    </Link>
  );

  if (!logo && !items?.length && !actions?.length) return null;

  return (
    <header className={cn("z-40 bg-daylight", sticky && "sticky top-0")} ref={headerRef}>
      <div className="daylight-container my-3 flex min-h-16 items-center gap-6 rounded-[22px] border border-border bg-daylight px-5">
        {logo ? (
          <Link
            aria-label="Home"
            className="shrink-0 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            href="/"
          >
            <DaylightMedia
              className="h-11 w-48 bg-transparent"
              imageClassName="object-contain object-left"
              media={logo}
              sizes="192px"
            />
          </Link>
        ) : null}
        <nav aria-label="Primary" className="ml-auto hidden items-center gap-1 lg:flex">
          {items?.map((item, index) =>
            item.blockType === "megaMenuDirectLink" ? (
              nestedLink(item.link, index)
            ) : (
              <div className="relative" key={index}>
                <button
                  aria-controls={`${navigationId}-desktop-${index}`}
                  aria-expanded={openDesktop === index}
                  className={cn(
                    linkClass,
                    "cursor-pointer gap-1",
                    containsCurrentLink(item) && "text-cobalt after:scale-x-100",
                  )}
                  onClick={() => setOpenDesktop(openDesktop === index ? null : index)}
                  type="button"
                >
                  {item.label}
                  <Icon
                    className={cn(
                      "size-4 transition-transform",
                      openDesktop === index && "rotate-180",
                    )}
                    name="chevron-down"
                  />
                </button>
                {openDesktop === index ? (
                  <div
                    className={cn(
                      "absolute right-0 top-full z-50 mt-3 min-w-64 rounded-[22px] border border-border bg-daylight p-2 shadow-[0_12px_30px_rgba(21,25,35,0.14)]",
                      item.blockType === "megaMenuGroup" &&
                        "grid min-w-[36rem] grid-cols-2 gap-4 p-4",
                    )}
                    id={`${navigationId}-desktop-${index}`}
                  >
                    {item.blockType === "megaMenuDropdown"
                      ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex))
                      : item.groupItems.map((group, groupIndex) => (
                          <div key={groupIndex}>
                            <p className="border-b border-weathered-steel px-3 py-2 font-daylight-mono text-xs text-primary">
                              {group.header}
                            </p>
                            {group.links.map((link, linkIndex) => nestedLink(link, linkIndex))}
                          </div>
                        ))}
                  </div>
                ) : null}
              </div>
            ),
          )}
        </nav>
        {actions?.length ? (
          <div className="hidden gap-2 lg:flex">
            {actions.map((action, index) => (
              <DaylightAction action={action} key={index} />
            ))}
          </div>
        ) : null}
        <button
          aria-controls={`${navigationId}-dialog`}
          aria-expanded={drawerOpen}
          aria-haspopup="dialog"
          aria-label="Open navigation menu"
          className="ml-auto inline-flex size-11 items-center justify-center rounded-full border border-border transition-colors hover:border-cobalt hover:bg-sky focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:hidden"
          onClick={() => setDrawerOpen(true)}
          type="button"
        >
          <Icon name="menu" />
        </button>
      </div>
      <dialog
        className="m-0 ml-auto h-dvh max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-charcoal-green-ink/45 md:w-[28rem]"
        id={`${navigationId}-dialog`}
        onClose={() => setDrawerOpen(false)}
        ref={dialogRef}
      >
        <div className="flex h-full w-full flex-col bg-mineral-gray p-6">
          <div className="flex items-center justify-between border-b border-weathered-steel pb-4">
            {logo ? (
              <Link
                aria-label="Home"
                className="rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                href="/"
                onClick={navigate}
              >
                <DaylightMedia
                  className="h-10 w-44 bg-transparent"
                  imageClassName="object-contain object-left"
                  media={logo}
                  sizes="176px"
                />
              </Link>
            ) : (
              <span className="font-daylight-mono text-xs">Menu</span>
            )}
            <button
              aria-label="Close navigation menu"
              className="inline-flex size-11 items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={() => setDrawerOpen(false)}
              type="button"
            >
              <Icon name="x" />
            </button>
          </div>
          <nav aria-label="Mobile primary" className="flex-1 overflow-y-auto py-4">
            {items?.map((item, index) =>
              item.blockType === "megaMenuDirectLink" ? (
                nestedLink(item.link, index, true)
              ) : (
                <div className="border-b border-weathered-steel" key={index}>
                  <button
                    aria-controls={`${navigationId}-mobile-${index}`}
                    aria-expanded={openMobile === index}
                    className={cn(
                      linkClass,
                      "w-full cursor-pointer justify-between",
                      containsCurrentLink(item) && "text-cobalt after:scale-x-100",
                    )}
                    onClick={() => setOpenMobile(openMobile === index ? null : index)}
                    type="button"
                  >
                    {item.label}
                    <Icon
                      className={cn(
                        "size-4 transition-transform",
                        openMobile === index && "rotate-180",
                      )}
                      name="chevron-down"
                    />
                  </button>
                  {openMobile === index ? (
                    <div className="pb-3 pl-3" id={`${navigationId}-mobile-${index}`}>
                      {item.blockType === "megaMenuDropdown"
                        ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex, true))
                        : item.groupItems.map((group, groupIndex) => (
                            <div key={groupIndex}>
                              <p className="px-3 py-2 font-daylight-mono text-xs text-primary">
                                {group.header}
                              </p>
                              {group.links.map((link, linkIndex) =>
                                nestedLink(link, linkIndex, true),
                              )}
                            </div>
                          ))}
                    </div>
                  ) : null}
                </div>
              ),
            )}
          </nav>
          {actions?.length ? (
            <div className="flex flex-col gap-2 border-t border-weathered-steel pt-4">
              {actions.map((action, index) => (
                <DaylightAction action={action} key={index} />
              ))}
            </div>
          ) : null}
        </div>
      </dialog>
    </header>
  );
};

MegaMenuNavigation.displayName = "Content::MegaMenuNavigation";
